import { adminFetch } from './dataProvider';
import { ConferenceDiscoveryQuestion, DiscoveryQuestion } from './types';

/** Body for create/update of a question (inventory or in-conference). */
export interface QuestionPayload {
  text: string;
  type: 'choice' | 'free_text';
  allowMultiple?: boolean;
  hasOtherOption?: boolean;
  required?: boolean;
  isDefault?: boolean;
  options?: { text: string }[];
}

/** company_admin omits companyId (token-scoped); optional override otherwise. */
function scope(companyId?: string): string {
  return companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
}

// ── Inventory ───────────────────────────────────────────────────────
export async function listQuestions(companyId?: string): Promise<DiscoveryQuestion[]> {
  const { questions } = await adminFetch<{ questions: DiscoveryQuestion[] }>(
    `/v1/admin/questions${scope(companyId)}`,
  );
  return questions;
}

export function getQuestion(id: string, companyId?: string): Promise<DiscoveryQuestion> {
  return adminFetch<DiscoveryQuestion>(`/v1/admin/questions/${id}${scope(companyId)}`);
}

export function createQuestion(payload: QuestionPayload, companyId?: string): Promise<DiscoveryQuestion> {
  return adminFetch<DiscoveryQuestion>(`/v1/admin/questions${scope(companyId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateQuestion(id: string, payload: Partial<QuestionPayload>, companyId?: string): Promise<DiscoveryQuestion> {
  return adminFetch<DiscoveryQuestion>(`/v1/admin/questions/${id}${scope(companyId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteQuestion(id: string, companyId?: string): Promise<void> {
  return adminFetch<void>(`/v1/admin/questions/${id}${scope(companyId)}`, { method: 'DELETE' });
}

// ── Conference set ──────────────────────────────────────────────────
export async function listConferenceQuestions(conferenceId: string, companyId?: string): Promise<ConferenceDiscoveryQuestion[]> {
  const { questions } = await adminFetch<{ questions: ConferenceDiscoveryQuestion[] }>(
    `/v1/admin/conferences/${conferenceId}/questions${scope(companyId)}`,
  );
  return questions;
}

export function createConferenceQuestion(
  conferenceId: string,
  payload: QuestionPayload,
  companyId?: string,
): Promise<{ conferenceQuestion: ConferenceDiscoveryQuestion; inventoryQuestion: DiscoveryQuestion }> {
  return adminFetch(`/v1/admin/conferences/${conferenceId}/questions${scope(companyId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function importInventory(
  conferenceId: string,
  body: { inventoryIds?: string[]; defaults?: true },
  companyId?: string,
): Promise<ConferenceDiscoveryQuestion[]> {
  const { questions } = await adminFetch<{ questions: ConferenceDiscoveryQuestion[] }>(
    `/v1/admin/conferences/${conferenceId}/questions/import${scope(companyId)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return questions;
}

export function updateConferenceQuestion(
  conferenceId: string,
  id: string,
  payload: Partial<QuestionPayload>,
  companyId?: string,
): Promise<ConferenceDiscoveryQuestion> {
  return adminFetch<ConferenceDiscoveryQuestion>(
    `/v1/admin/conferences/${conferenceId}/questions/${id}${scope(companyId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

export function reorderConferenceQuestions(conferenceId: string, orderedIds: string[], companyId?: string): Promise<{ orderedIds: string[] }> {
  return adminFetch<{ orderedIds: string[] }>(
    `/v1/admin/conferences/${conferenceId}/questions/reorder${scope(companyId)}`,
    { method: 'PATCH', body: JSON.stringify({ orderedIds }) },
  );
}

export function deleteConferenceQuestion(conferenceId: string, id: string, companyId?: string): Promise<void> {
  return adminFetch<void>(
    `/v1/admin/conferences/${conferenceId}/questions/${id}${scope(companyId)}`,
    { method: 'DELETE' },
  );
}

// ── Bulk rep assignment ─────────────────────────────────────────────
export function assignAllReps(conferenceId: string, companyId: string): Promise<{ assigned: number; candidates: number }> {
  return adminFetch(`/v1/admin/conferences/${conferenceId}/assignments:all`, {
    method: 'POST',
    body: JSON.stringify({ companyId }),
  });
}

export function removeAllReps(conferenceId: string, companyId: string): Promise<{ removed: number; candidates: number }> {
  return adminFetch(`/v1/admin/conferences/${conferenceId}/assignments:all`, {
    method: 'DELETE',
    body: JSON.stringify({ companyId }),
  });
}

// ── Per-rep assignment ──────────────────────────────────────────────
export function assignRep(conferenceId: string, userOid: string): Promise<void> {
  return adminFetch<void>(
    `/v1/admin/conferences/${conferenceId}/assignments/${userOid}`,
    { method: 'POST' },
  );
}

export function unassignRep(conferenceId: string, userOid: string): Promise<void> {
  return adminFetch<void>(
    `/v1/admin/conferences/${conferenceId}/assignments/${userOid}`,
    { method: 'DELETE' },
  );
}
